import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Shield } from 'lucide-react';
import { useRedeemUx } from './RedeemUxContext';

export interface IDTokenRedemptionResultProps {
  result: Record<string, unknown>;
  tokenKey: string;
  onClose: () => void;
}

const HIDDEN_KEYS = new Set([
  'userId',
  'user_id',
  '_id',
  'verified',
  'isIDToken',
  'tokenKey',
  'key',
  '__v',
  'createdAt',
  'updatedAt',
]);

const IDENTITY_FIELD_ORDER = [
  'firstName',
  'lastName',
  'username',
  'email',
  'telephone',
  'phone',
  'address',
] as const;

const META_FIELD_ORDER = ['scope', 'canCredit', 'expiresAt', 'expiry', 'message'] as const;

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  username: 'Username',
  email: 'Email',
  telephone: 'Phone',
  phone: 'Phone',
  address: 'Address',
  scope: 'Permissions',
  canCredit: 'Credit currencies',
  expiresAt: 'Expires at',
  expiry: 'Expires at',
  message: 'Message',
};

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatLocalDateTime(value: unknown): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatAddress(value: Record<string, unknown>): string {
  const parts = [
    value.line1,
    value.line2,
    value.street,
    value.city,
    value.state,
    value.region,
    value.postalCode,
    value.zip,
    value.country,
  ]
    .filter((part) => part != null && String(part).trim() !== '')
    .map((part) => String(part).trim());
  return parts.join(', ');
}

function formatDisplayValue(key: string, value: unknown): string {
  if (value == null || value === '') return '';

  if (key === 'expiresAt' || key === 'expiry') {
    return formatLocalDateTime(value);
  }

  if (Array.isArray(value)) {
    return value.length ? value.map(String).join(', ') : '';
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (key === 'address') {
      return formatAddress(obj);
    }
    const formatted = Object.entries(obj)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${humanizeKey(k)}: ${String(v)}`)
      .join(', ');
    return formatted;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

function pushRow(
  rows: { key: string; label: string; value: string }[],
  seen: Set<string>,
  key: string,
  value: unknown,
  labelFor: (key: string) => string
) {
  if (HIDDEN_KEYS.has(key) || seen.has(key)) return;
  const formatted = formatDisplayValue(key, value);
  if (!formatted) return;
  seen.add(key);
  rows.push({ key, label: labelFor(key), value: formatted });
}

export function buildIdTokenDisplayRows(
  result: Record<string, unknown>,
  labelFor: (key: string) => string = (key) => FIELD_LABELS[key] ?? humanizeKey(key)
): { key: string; label: string; value: string }[] {
  const rows: { key: string; label: string; value: string }[] = [];
  const seen = new Set<string>();
  const identity =
    result.userIdentity && typeof result.userIdentity === 'object'
      ? (result.userIdentity as Record<string, unknown>)
      : {};

  for (const key of IDENTITY_FIELD_ORDER) {
    pushRow(rows, seen, key, identity[key], labelFor);
  }

  for (const [key, value] of Object.entries(identity)) {
    if ((IDENTITY_FIELD_ORDER as readonly string[]).includes(key)) continue;
    pushRow(rows, seen, key, value, labelFor);
  }

  for (const key of META_FIELD_ORDER) {
    if (key === 'expiry' && seen.has('expiresAt')) continue;
    const value =
      key === 'canCredit' || key === 'scope' || key === 'message' || key === 'expiresAt' || key === 'expiry'
        ? result[key] ?? identity[key]
        : result[key];
    pushRow(rows, seen, key === 'expiry' ? 'expiresAt' : key, value, labelFor);
  }

  return rows;
}

const IDTokenRedemptionResult: React.FC<IDTokenRedemptionResultProps> = ({
  result,
  onClose,
}) => {
  const { i18n } = useRedeemUx();

  const labelFor = (key: string): string => {
    const i18nKey = `redeemToken.idField.${key}`;
    const translated = i18n.__(i18nKey);
    if (translated !== i18nKey) return translated;
    return FIELD_LABELS[key] ?? humanizeKey(key);
  };

  const rows = buildIdTokenDisplayRows(result, labelFor);

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center space-x-2 text-blue-800">
          <Shield className="w-5 h-5" />
          <span>{i18n.__('redeemToken.idTokenVerified')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {i18n.__('redeemToken.idTokenRedeemedSuccess')}
        </p>
        {rows.length > 0 && (
          <dl className="space-y-2">
            {rows.map(({ key, label, value }) => (
              <div
                key={key}
                className="grid grid-cols-[minmax(0,38%)_1fr] gap-x-4 gap-y-1 p-3 bg-white rounded-lg border text-sm"
              >
                <dt className="text-muted-foreground font-medium">{label}</dt>
                <dd className="text-foreground break-words">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        <Button onClick={onClose} className="w-full mt-4">
          {i18n.__('redeemToken.continue')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default IDTokenRedemptionResult;
